<template>
	<form class="col-md-8 mx-auto" @submit.prevent="onSubmit">
		<div class="loader" v-if="loading"></div>
		<div v-show="!loading">
			<div class="btn-group" role="group" aria-label="Basic example">
				<button type="button" class="btn btn-primary" @click="backToList">
					<i class="fas fa-th-list"></i>
					{{ $t('backToList') }}
				</button>
			</div>
			<page-title :title="$t('notificationConfig.title')"></page-title>
			<div class="card row">
				<div class="card-header bg-primary text-white">
					<div class="card-title h5 m-0">{{ $t('notificationConfig.config') }}</div>
				</div>
				<div class="card-body">
					<div class="row">
						<div class="form-group col-12">
							<label :for="'notificationType' + _uid" class="font-weight-bold required">{{ $t('notificationConfig.type') }}</label>
							<input
								type="text"
								class="form-control"
								readonly
								:id="'eventType' + _uid"
								:name="'eventType' + _uid"
								v-model="notificationConfig.notificationType"
								:data-vv-as="$t('notificationConfig.type')"
							/>
							<span class="text-danger" v-if="errors.has('notificationType' + _uid)">{{ errors.first('notificationType' + _uid) }}</span>
						</div>
					</div>
					<div class="row">
						<div class="form-group col-12">
							<label :for="'notificationType' + _uid">{{ $t('notificationConfig.keywordsDescription') }}</label>
							<textarea class="form-control" readonly v-model="notificationConfig.keywords" :data-vv-as="$t('notificationConfig.keywords')" rows="8"></textarea>
						</div>
					</div>
					<div class="row">
						<div class="form-group col-12">
							<label :for="'subject' + _uid" class="font-weight-bold required">{{ $t('notificationConfig.subject') }}</label>
							<input
								type="text"
								class="form-control"
								:id="'subject' + _uid"
								:name="'subject' + _uid"
								v-model="notificationConfig.messageSubject"
								:data-vv-as="$t('notificationConfig.subject')"
							/>
							<span class="text-danger" v-if="errors.has('subject' + _uid)">{{ errors.first('subject' + _uid) }}</span>
						</div>
					</div>

					<div class="row">
						<div class="form-group col-12">
							<label :for="'content' + _uid" class="font-weight-bold required">{{ $t('notificationConfig.content') }}</label>
							<editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
								<div class="menubar">
									<button type="button" class="menubar__button" :class="{ 'is-active': isActive.bold() }" @click="commands.bold">
										<i class="fas fa-bold"></i>
									</button>

									<button type="button" class="menubar__button" :class="{ 'is-active': isActive.italic() }" @click="commands.italic">
										<i class="fas fa-italic"></i>
									</button>

									<button type="button" class="menubar__button" :class="{ 'is-active': isActive.underline() }" @click="commands.underline">
										<i class="fas fa-underline"></i>
									</button>

									<button type="button" class="menubar__button" :class="{ 'is-active': isActive.bullet_list() }" @click="commands.bullet_list">
										<i class="fas fa-list-ul"></i>
									</button>

									<button type="button" class="menubar__button" :class="{ 'is-active': isActive.ordered_list() }" @click="commands.ordered_list">
										<i class="fas fa-list-ol"></i>
									</button>

									<button type="button" class="menubar__button" :class="{ 'is-active': isActive.heading({ level: 1 }) }" @click="commands.heading({ level: 1 })">H1</button>

									<button type="button" class="menubar__button" :class="{ 'is-active': isActive.heading({ level: 2 }) }" @click="commands.heading({ level: 2 })">H2</button>

									<button type="button" class="menubar__button" :class="{ 'is-active': isActive.heading({ level: 3 }) }" @click="commands.heading({ level: 3 })">H3</button>
								</div>
							</editor-menu-bar>
							<editor-content :editor="editor" class="editorContent" />
						</div>
					</div>
				</div>
			</div>

			<div class="text-center mt-2 mb-5">
				<button type="button" class="btn btn-primary btn" @click="onSubmit">
					<i class="fas fa-save"></i>
					{{ $t('buttons.save.btnText') }}
				</button>
			</div>
		</div>
	</form>
</template>

<script>
import { NotificationConfig } from '../../../models/models';
import PageTitle from '../../../components/common/PageTitle';

import { Editor, EditorContent, EditorMenuBar } from 'tiptap';
import { Heading, OrderedList, BulletList, ListItem, Bold, Italic, Link, Underline } from 'tiptap-extensions';

export default {
	components: {
		PageTitle,
		EditorContent,
		EditorMenuBar
	},
	data() {
		return {
			id: null,
			notificationConfig: new NotificationConfig(),
			loading: false,
			editor: new Editor({
				extensions: [
					new BulletList(),
					new Heading({ levels: [1, 2, 3] }),
					new ListItem(),
					new OrderedList(),
					new Link(),
					new Bold(),
					new Italic(),
					new Underline()
				],
				content: 'Message body',
				onUpdate: ({ getJSON, getHTML }) => {
					this.json = getJSON();
					this.html = getHTML();
				}
			}),
			json: '',
			html: ''
		};
	},
	methods: {
		onSubmit() {
			this.notificationConfig.messageContent = this.editor.getHTML();

			this.$validator.validateAll().then(result => {
				if (result) {
					if (this.id) {
						this.edit();
					}
				}
			});
		},
		backToList() {
			this.$router.push('/administration/notifications');
		},

		edit() {
			this.loading = true;
			this.$http
				.put('/api/notificationConfig', this.notificationConfig)
				.then(result => {
					this.loading = false;
					this.$vs.notify({
						title: this.$t('notifications.success'), // TODO:
						text: this.$t('notifications.successfulSavedData'), // TODO:
						position: 'top-center',
						color: 'success'
					});
					this.backToList();
				})
				.catch(error => {
					this.loading = false;
					this.$vs.notify({
						title: this.$t('notifications.error'), // TODO:
						text: this.$t('notifications.errorDataNotSaved'), // TODO:
						position: 'top-center',
						color: 'danger'
					});
				});
		},
		loadData() {
			this.loading = true;
			var self = this;
			this.$http
				.get(`/api/notificationConfig/${this.id}`)
				.then(result => {
					self.notificationConfig = new NotificationConfig(result.data);

					self.editor.setContent(self.notificationConfig.messageContent, true);

					self.loading = false;
				})
				.catch(error => {
					// TODO: Notify
					this.loading = false;
				});
		}
	},
	mounted() {
		this.id = this.$route.params.id;
		if (this.id) {
			this.loadData();
		}
	},
	beforeDestroy() {
		this.editor.destroy();
	}
};
</script>

<style scoped>
.menubar__button {
	margin-right: 10px;
}

.menubar {
	margin-bottom: 10px;
}

/*div .editorContent {
        line-height: 1px;
        margin-top: 20px;
    }*/
</style>
